/**
 * @file add-attach-button.component.tsx
 * @description Компонент группы кнопок для добавления различных типов вложений в Task Manager приложении.
 * Предоставляет три способа прикрепления данных:
 * загрузка файлов через файловый диалог с автоматической отправкой на сервер,
 * добавление ссылок через модальное окно с формой ввода URL,
 * выбор объектов из системного каталога через компонент Catalog.
 *  Каждый тип вложения автоматически категоризируется с соответствующим EAttachmentType (Файл/Ссылка/Объект)
 * и добавляется в переданный массив value через onChange callback для интеграции с формами и rich-text редакторами.
 * @author AI-Generated | Human-Reviewed
 * @created 2025-01-15
 * @updated 2025-09-18
 * @dependencies ['react', '@consta/icons/IconAdd', '@components/ui/base/button', '@consta/icons/IconConnection', '@components/icons/attach-file', '@forms/modals/link.modal', '@api/actions/file', '@features/catalog', '@utils/data-helper']
 * @related-files ['src/components/icons/attach-file', 'src/forms/modals/link.modal.tsx', 'src/api/actions/file.ts', 'src/features/catalog', 'src/types/attachment.ts']
 * TODO: переименовать компонент в AttachmentButtonsGroup
 */

import { useRef, useState } from 'react';
import { IconAdd } from '@consta/icons/IconAdd';
import { Button } from '@components/ui/base/button';
import { IconConnection } from '@consta/icons/IconConnection';
import { AttachFileIcon } from '@components/icons/attach-file';
import { LinkModal } from '@forms/modals/link.modal';
import { FileService } from '@api/actions/file';
import { Catalog } from '@features/catalog';
import { TAttachment } from '@src/types';
import { ITableItemDTO } from '@src/types/dto';
import {
    createNewFileFromUploaded,
    createNewLink,
    createNewSystemObject,
    convertNewAttachmentToFull
} from '@utils/data-helper';

/**
 * Props для компонента AddAttachButton (группы кнопок добавления вложений)
 */
interface IAddAttachButtonProps {
    /** Массив текущих вложений */
    value?: TAttachment[];
    /** Callback функция вызываемая при изменении списка вложений */
    onChange: (attachments: TAttachment[]) => void;
}

export const AddAttachButton = ({ value, onChange }: IAddAttachButtonProps) => {
    const anchorRef = useRef<HTMLDivElement>(null);
    const [isShowLinkModal, setIsShowLinkModal] = useState<boolean>(false);
    const hiddenFileInput = useRef<HTMLInputElement>(null);
    const [isShowObjectModal, setIsShowObjectModal] = useState<boolean>(false);

    const handleClick = (_event: React.MouseEvent<HTMLButtonElement>) => {
        _event.stopPropagation();
        _event.preventDefault();
        hiddenFileInput.current?.click();
    };

    const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        event.stopPropagation();
        event.preventDefault();
        const files = event.target.files;
        if (!files || files.length === 0) return;

        const fileUploaded = files[0];
        const formData = new FormData();
        formData.append('file', fileUploaded);
        const file = await FileService.create(formData);

        if (!file) {
            return;
        }

        const uploadedFile = {
            link: file.link,
            name: file.name || fileUploaded.name,
            size: file.size || fileUploaded.size,
            createdAt: Date.now(),
            isExist: true,
            createdBy: "Текущий пользователь"
        };

        const newFile = createNewFileFromUploaded(uploadedFile);
        const attachment = convertNewAttachmentToFull(newFile, {
            id: undefined,
            createdBy: "Текущий пользователь"
        });

        onChange([...(value || []), attachment]);
    };

    return (
        <>
            <div className="w-fit flex gap-default" ref={anchorRef}>
                <Button
                    className="text-[1.482vmin]"
                    iconLeft={() => <AttachFileIcon className='mr-[var(--button-icon-indent)]' />}
                    label="Прикрепить файлы"
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleClick(e)}
                    view="ghost"
                />

                <Button
                    className="text-[1.482vmin]"
                    iconLeft={IconConnection}
                    label="Добавить ссылку"
                    onClick={() => setIsShowLinkModal(true)}
                    view="ghost"
                />

                <Button
                    className="text-[1.482vmin]"
                    iconLeft={IconAdd}
                    label="Добавить объект"
                    onClick={() => setIsShowObjectModal(true)}
                    view="ghost"
                />
            </div>

            {/* Блок ссылок */}
            {isShowLinkModal && (
                <LinkModal
                    isShow={isShowLinkModal}
                    title="Добавить ссылку"
                    setIsShow={setIsShowLinkModal}
                    addItemHandler={(data: TAttachment) => {
                        const newLink = createNewLink(data.link, data.name);
                        const attachment = convertNewAttachmentToFull(newLink, {
                            id: undefined,
                            createdBy: "Текущий пользователь"
                        });
                        onChange([...(value || []), attachment]);
                    }}
                />
            )}

            {/* Блок файлов */}
            <input
                type="file"
                className="collapse-control fixed top-0 left-0"
                onChange={handleChange}
                ref={hiddenFileInput}
                style={{ display: 'none' }}
            />

            {/* Блок объектов */}
            {isShowObjectModal && (
                <Catalog
                    stepsCount={2}
                    onClose={() => setIsShowObjectModal(false)}
                    type="AttachmentObject"
                    onRowClick={(item: ITableItemDTO) => {
                        const newSystemObject = createNewSystemObject(
                            item.$item,
                            item.name || item.$item.name
                        );
                        const attachment = convertNewAttachmentToFull(newSystemObject, {
                            id: undefined,
                            createdBy: "Текущий пользователь"
                        });
                        onChange([...(value || []), attachment]);
                    }}
                />
            )}
        </>
    );
};
